import React, { useState } from 'react';
import cs from 'classnames';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  Placement,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { useMediaQuery } from 'react-responsive';
import { breakpoints } from 'shared/styles/styleFunctions';
import { Flex, Box } from 'shared/components/display';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons';

import styles from './styles';
import Tooltip from 'shared/components/atoms/Tooltip/NewTooltip';

interface Props {
  className?: string;
  children: React.ReactElement | React.ReactElement[];
  portalTarget?: string;
  dataCy?: string;
  placement?: Placement;
  width?: string | number;
  disabled?: boolean;
  debug?: boolean;
  menuClassName?: string;
  ButtonContent: React.ReactElement;
  onClick?: () => void;
  allRounded?: boolean;
  disabledTooltip?: string;
  dontHideOnClick?: boolean;
  maxHeight?: string;
  hideDropdown?: () => void; // passed from the parent Dropdown component to allow child component to close the dropdown
  css?: any;
}
const SubDropdownButton: React.FC<Props> = ({
  className,
  children,
  portalTarget,
  dataCy,
  placement = 'right-start',
  width,
  disabled,
  debug = false,
  menuClassName,
  ButtonContent,
  onClick,
  allRounded,
  disabledTooltip,
  dontHideOnClick = false,
  maxHeight,
  hideDropdown: hideParentDropdown,
  css,
}) => {
  const [isOpen, setIsOpen] = useState(debug);
  const isMobile = useMediaQuery({
    maxWidth: breakpoints.md - 1,
  });

  const onOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen || debug,
    onOpenChange,
    middleware: [offset(2), flip(), shift()],
    whileElementsMounted: autoUpdate,
    ...(placement ? { placement } : {}),
  });

  const hover = useHover(context, {
    move: true,
    enabled: !disabled && !isMobile,
    handleClose: safePolygon(), // allows mouse to traverse from button to dropdown without closing dropdown
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'menu' });
  const click = useClick(context, { enabled: !disabled && isMobile, event: 'click' });

  const { getReferenceProps, getFloatingProps } = useInteractions([click, hover, focus, dismiss, role]);

  const portalId = typeof portalTarget === 'string' ? portalTarget : undefined;
  const portalRoot = typeof portalTarget !== 'string' ? portalTarget : undefined;

  const hideDropdown = () => {
    onOpenChange(false);
  };

  return (
    <>
      {React.cloneElement(
        <Box>
          <Tooltip disabled={!disabled || !Boolean(disabledTooltip)} content={disabledTooltip}>
            <Flex
              css={[styles(width), css]}
              className={cs('sub-dropdown-button', className, {
                'sub-dropdown-button--disabled': disabled,
              })}
            >
              {ButtonContent}
              <FontAwesomeIcon icon={faAngleRight} className="sub-dropdown-button__caret" />
            </Flex>
          </Tooltip>
        </Box>,
        getReferenceProps({
          ref: refs.setReference,
          //   @ts-ignore
          'data-state': isOpen || debug ? 'open' : 'closed',
          'data-cy': dataCy,
        }),
      )}
      {(isOpen || debug) && (
        <FloatingPortal id={portalId} root={portalRoot}>
          <Box
            css={[styles(width, maxHeight), css]}
            className={cs('sub-dropdown-button__menu', menuClassName, {
              'sub-dropdown-button__menu--all-rounded': allRounded,
              'sub-dropdown-button__menu--open': isOpen || debug,
            })}
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {React.Children.map(children, (Child: React.ReactElement) => {
              if (!Child) return null;
              const extraProps = {
                className: cs('sub-dropdown-button__option', 'endWithEllipsis', Child.props.className),
                type: 'button',
                variant: Child.props.variant,
                onClick: (e) => {
                  e?.persist();
                  if (Child.props.onClick) Child.props.onClick(e);
                  if (onClick) onClick();
                  if (!dontHideOnClick) hideDropdown();
                },
                hideParentDropdown: () => {
                  hideParentDropdown?.();
                  hideDropdown();
                },
              };
              return React.cloneElement(Child, extraProps);
            })}
          </Box>
        </FloatingPortal>
      )}
    </>
  );
};

export default SubDropdownButton;
